"use client";

/**
 * Generic leaderboard page — June 2026 redesign.
 *
 * Visual: dark-slate hero with radial blue glow, podium cards for top 3,
 * "your position" banner, stats strip, and tabular list with percentile.
 * Matches public/mockup/leaderboard.html closely.
 *
 * Data: three Supabase views (leaderboard_alltime / weekly / thisyear),
 * percentile computed client-side from rank ÷ total.
 */
import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter, useParams } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { type ExamType, EXAM_CONFIGS } from "@/lib/exam/config";
import { LandingNav } from "../../landing-nav";
import { Crown, Medal, Inbox } from "lucide-react";

interface LeaderboardEntry {
  display_name: string | null;
  user_id: string;
  exam_type: string;
  best_score: number;
  exams_taken?: number;
}

type Period = "alltime" | "weekly" | "thisyear";

function fmtScore(raw: number, examType: string): string {
  return examType === "ielts" ? (raw / 10).toFixed(1) : String(raw);
}
function fmtMax(examType: string, max: number): string {
  return examType === "ielts" ? "/9.0" : `/${max}`;
}
function getInitials(name: string | null): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

const BG_PALETTE  = ["#EFF6FF","#F0FDF4","#FFF7ED","#FDF4FF","#FFF1F2","#FEFCE8","#F0FDFA"];
const TXT_PALETTE = ["#2563EB","#16A34A","#EA580C","#9333EA","#E11D48","#CA8A04","#0D9488"];
function stableHash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}
function colorFor(seed: string)     { return BG_PALETTE[stableHash(seed)  % BG_PALETTE.length]; }
function colorTextFor(seed: string) { return TXT_PALETTE[stableHash(seed) % TXT_PALETTE.length]; }

/** Percentile as "Top X%" string (1-based rank in list of total). */
function pctLabel(rank: number, total: number): string {
  if (total === 0) return "—";
  const pct = Math.ceil((rank / total) * 100);
  if (pct <= 1) return "Top 1%";
  return `Top ${pct}%`;
}

// ─── Podium card (top 3) ────────────────────────────────────────────────────

const PODIUM_META = [
  { rank: 1, crown: Crown,  label: "#1",       border: "#F59E0B", glow: "rgba(245,158,11,.12)", avatarBg: "#FFFBEB", avatarBorder: "#FDE68A", avatarText: "#D97706", scoreCls: "text-[38px]" },
  { rank: 2, crown: Medal,  label: "#2",       border: "#CBD5E1", glow: "transparent",           avatarBg: "#F1F5F9", avatarBorder: "#E2E8F0", avatarText: "#64748B", scoreCls: "text-[30px]" },
  { rank: 3, crown: Medal,  label: "#3",       border: "#D97706", glow: "transparent",           avatarBg: "#FFF7ED", avatarBorder: "#FED7AA", avatarText: "#B45309", scoreCls: "text-[30px]" },
] as const;

function PodiumCard({
  entry, rank, total, isMe, examType, scoreMax,
}: {
  entry: LeaderboardEntry; rank: number; total: number; isMe: boolean;
  examType: string; scoreMax: number;
}) {
  const m = PODIUM_META[rank - 1];
  const order = rank === 2 ? "sm:order-first" : rank === 3 ? "sm:order-last" : "sm:order-2";
  return (
    <div
      className={`relative flex flex-col items-center text-center rounded-2xl border bg-white px-5 py-6 transition-transform hover:-translate-y-1
        ${rank === 1 ? "sm:pb-8 sm:pt-8" : ""} ${order}`}
      style={{
        borderColor: m.border,
        boxShadow: rank === 1 ? `0 0 0 1px ${m.border}, 0 20px 40px ${m.glow}` : undefined,
        width: rank === 1 ? undefined : undefined,
      }}
    >
      {/* Crown badge */}
      <div
        className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full flex items-center justify-center border-2 bg-white"
        style={{ borderColor: m.border }}
      >
        <m.crown className="w-4 h-4" style={{ color: m.border }} strokeWidth={1.75} aria-hidden="true" />
      </div>

      {/* Rank label */}
      <div
        className="mt-2 mb-3 text-[11px] font-extrabold uppercase tracking-widest"
        style={{ color: m.border }}
      >
        {m.label}
      </div>

      {/* Avatar */}
      <div
        className="flex items-center justify-center rounded-full font-extrabold mb-3"
        style={{
          width: rank === 1 ? 60 : 52,
          height: rank === 1 ? 60 : 52,
          background: isMe ? "#EFF6FF" : m.avatarBg,
          color: isMe ? "#1D4ED8" : m.avatarText,
          border: `2px solid ${isMe ? "#BFDBFE" : m.avatarBorder}`,
          fontSize: rank === 1 ? 18 : 15,
        }}
      >
        {getInitials(entry.display_name)}
      </div>

      {/* Name */}
      <div className={`font-bold text-[14px] truncate max-w-full mb-0.5 ${isMe ? "text-blue-700" : "text-gray-900"}`}>
        {entry.display_name ?? "Anonymous"}
        {isMe && <span className="ml-1.5 align-middle text-[9px] bg-blue-600 text-white px-1.5 py-0.5 rounded-full font-bold uppercase">You</span>}
      </div>

      {/* Score */}
      <div className={`font-black tabular-nums leading-none mt-1 ${m.scoreCls} ${rank === 1 ? "text-blue-700" : "text-gray-800"}`}>
        {fmtScore(entry.best_score, examType)}
      </div>
      <div className="text-[11px] text-gray-400 mt-0.5">{fmtMax(examType, scoreMax)}</div>

      {/* Percentile */}
      <div className="mt-3 text-[12px] font-semibold text-emerald-600">{pctLabel(rank, total)}</div>
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────

export default function GenericLeaderboardPage() {
  const params = useParams();
  const examType = (params.examType as string) ?? "toefl";
  const config = EXAM_CONFIGS[examType as ExamType];

  const [allTime, setAllTime]   = useState<LeaderboardEntry[]>([]);
  const [weekly, setWeekly]     = useState<LeaderboardEntry[]>([]);
  const [thisYear, setThisYear] = useState<LeaderboardEntry[]>([]);
  const [period, setPeriod]     = useState<Period>("alltime");
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [loading, setLoading]   = useState(true);
  const [authed, setAuthed]     = useState(false);
  const [availableExams, setAvailableExams] = useState<string[]>([]);
  const router = useRouter();

  useEffect(() => {
    const supabase = createClient();
    async function load() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { router.replace(`/auth?redirect=/leaderboard/${examType}`); return; }
      setAuthed(true);
      setCurrentUserId(user.id);

      const [atRes, wRes, tyRes] = await Promise.all([
        supabase.from("leaderboard_alltime").select("*").eq("exam_type", examType).order("best_score", { ascending: false }).limit(100),
        supabase.from("leaderboard_weekly").select("*").eq("exam_type", examType).order("best_score", { ascending: false }).limit(100),
        supabase.from("leaderboard_thisyear").select("*").eq("exam_type", examType).order("best_score", { ascending: false }).limit(100),
      ]);
      setAllTime((atRes.data ?? []) as LeaderboardEntry[]);
      setWeekly((wRes.data ?? []) as LeaderboardEntry[]);
      setThisYear((tyRes.data ?? []) as LeaderboardEntry[]);

      const { data: distinct } = await supabase.from("leaderboard_alltime").select("exam_type");
      if (distinct) {
        const types = Array.from(new Set(distinct.map((r: { exam_type: string }) => r.exam_type)));
        setAvailableExams(types);
      }
      setLoading(false);
    }
    load();
  }, [examType, router]);

  if (!config) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
        <div className="text-center text-white">
          <h1 className="text-2xl font-extrabold mb-2">Unknown exam</h1>
          <p className="text-gray-400 text-sm mb-4">No leaderboard exists for &ldquo;{examType}&rdquo;.</p>
          <Link href="/leaderboard/toefl" className="text-blue-400 hover:underline font-semibold text-sm">→ TOEFL leaderboard</Link>
        </div>
      </div>
    );
  }

  if (!authed) return null;

  const data    = period === "alltime" ? allTime : period === "weekly" ? weekly : thisYear;
  const total   = data.length;
  const myRank  = data.findIndex(e => e.user_id === currentUserId) + 1;
  const myEntry = data.find(e => e.user_id === currentUserId);
  const scores  = data.map(e => e.best_score);
  const avgScore = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;
  const topScore = scores.length > 0 ? Math.max(...scores) : null;
  const perfectScores = scores.filter(s => s >= config.v1ScoreMax).length;

  const PERIODS = [
    { key: "alltime"  as Period, label: "All time" },
    { key: "thisyear" as Period, label: "This year" },
    { key: "weekly"   as Period, label: "This week" },
  ];

  return (
    <div className="min-h-screen bg-cream text-gray-900">
      <LandingNav />

      {/* ── DARK HERO ─────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#1E293B] to-[#0F172A]">
        {/* Blue radial glow */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(37,99,235,.18) 0%, transparent 70%)" }} />

        <div className="relative max-w-[1000px] mx-auto px-6 sm:px-12 pt-14 pb-0 text-center">
          {/* Eyebrow */}
          <div className="inline-flex items-center gap-2 text-blue-400 text-[11px] font-bold uppercase tracking-[.1em] mb-5">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
            Global Rankings
          </div>
          <h1 className="text-[44px] sm:text-[56px] font-black tracking-tight leading-[1.04] text-white mb-4">
            See where<br />
            <span className="italic" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>you stand.</span>
          </h1>
          <p className="text-[16px] text-slate-400 mb-10">
            Top practice scores across all exams. Compete, climb, beat your previous best.
          </p>
        </div>

        {/* Filter bar — attached to bottom of hero, bleeds into content */}
        <div className="relative max-w-[1000px] mx-auto px-6 sm:px-12">
          <div className="flex items-center justify-between gap-3 bg-white/[.06] border border-white/[.10] rounded-t-2xl px-6 py-4 overflow-x-auto">
            {/* Exam tabs — flagship exams first, then the rest alphabetically */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {availableExams
                .map(t => ({ key: t, cfg: EXAM_CONFIGS[t as ExamType] }))
                .filter(e => e.cfg)
                .sort((a, b) => {
                  const FLAGSHIP = ["toefl", "ielts", "sat", "act", "gre"];
                  const ai = FLAGSHIP.indexOf(a.key), bi = FLAGSHIP.indexOf(b.key);
                  if (ai !== -1 || bi !== -1) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
                  return a.cfg.name.localeCompare(b.cfg.name);
                })
                .map(({ key, cfg }) => {
                  const isActive = key === examType;
                  return (
                    <Link
                      key={key}
                      href={`/leaderboard/${key}`}
                      className={`px-3 py-2 rounded-lg text-[13px] font-medium transition-colors whitespace-nowrap
                        ${isActive
                          ? "bg-blue-600/25 text-blue-300 font-semibold"
                          : "text-slate-400 hover:text-white hover:bg-white/[.06]"
                        }`}
                    >
                      {cfg.name.replace(/ iBT| Academic| General/i, "")}
                    </Link>
                  );
                })}
            </div>

            {/* Period picker */}
            <select
              aria-label="Time period"
              value={period}
              onChange={e => setPeriod(e.target.value as Period)}
              className="h-9 px-3 text-[13px] rounded-lg border bg-white/[.07] border-white/[.12] text-slate-300 outline-none cursor-pointer"
              style={{ fontFamily: "inherit" }}
            >
              {PERIODS.map(p => (
                <option key={p.key} value={p.key} style={{ background: "#1E293B", color: "#fff" }}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ── CONTENT ───────────────────────────────────────────────────────── */}
      <div className="max-w-[1000px] mx-auto px-6 sm:px-12 pb-20">

        {/* ── PODIUM (top 3) ── */}
        {!loading && data.length >= 1 && (
          <div className="flex flex-col sm:flex-row items-end justify-center gap-4 pt-10 pb-8">
            {/* Render order: 2nd left, 1st center, 3rd right */}
            {[1, 0, 2].map(i => {
              if (i >= data.length) return (
                <div key={i} className="w-full sm:w-[200px]" />
              );
              const entry = data[i];
              return (
                <div
                  key={entry.user_id}
                  className={`w-full sm:w-[${i === 0 ? "220" : "200"}px]`}
                  style={{ order: i === 0 ? 2 : i === 1 ? 1 : 3 }}
                >
                  <PodiumCard
                    entry={entry}
                    rank={i + 1}
                    total={total}
                    isMe={entry.user_id === currentUserId}
                    examType={examType}
                    scoreMax={config.v1ScoreMax}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* ── YOUR POSITION ── */}
        {!loading && myEntry && myRank > 0 && (
          <div className="flex items-center gap-4 bg-blue-50 border border-blue-200 rounded-xl px-5 py-4 mb-6 flex-wrap">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-blue-600 mb-0.5">Your position</div>
              <div className="text-[22px] font-black text-blue-700 leading-none">#{myRank}</div>
            </div>
            <div className="w-px h-10 bg-blue-200 self-stretch" />
            <div>
              <div className="text-[20px] font-extrabold text-gray-900">
                {fmtScore(myEntry.best_score, examType)}
                <span className="text-gray-400 text-[13px] font-medium">{fmtMax(examType, config.v1ScoreMax)}</span>
              </div>
              <div className="text-[12px] text-gray-400">{config.name}</div>
            </div>
            <div className="w-px h-10 bg-blue-200 self-stretch" />
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-blue-600 mb-0.5">Percentile</div>
              <div className="text-[16px] font-bold text-gray-800">{pctLabel(myRank, total)}</div>
            </div>
            {myRank > 1 && (
              <>
                <div className="w-px h-10 bg-blue-200 self-stretch" />
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-widest text-blue-600 mb-0.5">Next rank</div>
                  <div className="text-[13px] font-semibold text-gray-700">
                    {(() => {
                      const above = data[myRank - 2];
                      const gap = above.best_score - myEntry.best_score + (examType === "ielts" ? 5 : 1);
                      return `${fmtScore(gap, examType)} point${gap === 1 ? "" : "s"} to #${myRank - 1}`;
                    })()}
                  </div>
                </div>
              </>
            )}
            <Link
              href={`/${examType}`}
              className="ml-auto bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-bold px-4 py-2 rounded-lg transition-colors whitespace-nowrap"
            >
              Beat your score →
            </Link>
          </div>
        )}

        {/* ── STATS STRIP ── */}
        {!loading && total > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              { label: "Participants",     value: total.toString(),  sub: `${period === "weekly" ? "this week" : period === "thisyear" ? "this year" : "all time"}` },
              { label: `Top score`,        value: topScore != null ? fmtScore(topScore, examType) : "—", sub: fmtMax(examType, config.v1ScoreMax).replace("/", "out of ") },
              { label: "Average score",   value: avgScore != null ? fmtScore(avgScore, examType) : "—", sub: fmtMax(examType, config.v1ScoreMax).replace("/", "out of ") },
              { label: "Perfect scores",  value: perfectScores > 0 ? String(perfectScores) : "0", sub: "100% attained" },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-cream rounded-xl px-5 py-4">
                <div className="text-[11px] text-gray-400 font-medium mb-1">{s.label}</div>
                <div className="text-[24px] font-black tracking-tight text-gray-900">{s.value}</div>
                <div className="text-[12px] text-gray-400 mt-0.5">{s.sub}</div>
              </div>
            ))}
          </div>
        )}

        {/* ── TABLE ── */}
        {loading ? (
          <div className="bg-white border border-cream rounded-xl py-16 text-center text-gray-400 text-sm">
            Loading…
          </div>
        ) : data.length === 0 ? (
          <div className="bg-white border border-cream rounded-xl p-14 text-center">
            <div className="mb-3"><Inbox className="w-6 h-6 text-gray-300 inline-block" strokeWidth={1.75} aria-hidden="true" /></div>
            <div className="text-[15px] font-semibold mb-1">No scores yet for this period.</div>
            <div className="text-[13px] text-gray-400">Be the first — start a practice exam.</div>
            <Link href={`/${examType}`} className="mt-4 inline-block text-blue-600 font-semibold text-sm hover:underline">
              Take a practice test →
            </Link>
          </div>
        ) : (
          <div className="bg-white border border-cream rounded-xl overflow-hidden">
            {/* Table header */}
            <div className="grid gap-0 px-5 py-3 border-b border-cream bg-cream"
              style={{ gridTemplateColumns: "56px 1fr 120px 100px" }}>
              {["Rank", "Student", "Score", "Percentile"].map(h => (
                <span key={h} className="text-[11px] font-bold uppercase tracking-widest text-gray-400">{h}</span>
              ))}
            </div>

            {data.slice(data.length > 3 ? 3 : 0).map((entry, i) => {
              const idx   = (data.length > 3 ? i + 3 : i) + 1;
              const isMe  = entry.user_id === currentUserId;
              const ratio = entry.best_score / config.v1ScoreMax;
              return (
                <div
                  key={entry.user_id}
                  className={`grid items-center gap-0 px-5 py-3.5 border-b border-cream last:border-0 transition-colors cursor-default
                    ${isMe ? "bg-blue-50" : "hover:bg-[#FAFAF8]"}`}
                  style={{ gridTemplateColumns: "56px 1fr 120px 100px" }}
                >
                  {/* Rank */}
                  <div className={`text-[14px] font-bold ${isMe ? "text-blue-600" : "text-gray-400"}`}>
                    {idx}
                  </div>

                  {/* Student */}
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-extrabold text-[12px]"
                      style={{
                        background: isMe ? "#EFF6FF" : colorFor(entry.user_id),
                        color: isMe ? "#1D4ED8" : colorTextFor(entry.user_id),
                        border: isMe ? "2px solid #BFDBFE" : undefined,
                      }}
                    >
                      {getInitials(entry.display_name)}
                    </div>
                    <div className="min-w-0">
                      <div className={`font-semibold text-[14px] truncate ${isMe ? "text-blue-700" : "text-gray-900"}`}>
                        {entry.display_name ?? "Anonymous"}
                        {isMe && (
                          <span className="ml-2 align-middle text-[9px] bg-blue-600 text-white px-1.5 py-0.5 rounded-full font-bold uppercase">You</span>
                        )}
                      </div>
                      {entry.exams_taken != null && (
                        <div className="text-[11px] text-gray-400">{entry.exams_taken} attempt{entry.exams_taken !== 1 ? "s" : ""}</div>
                      )}
                    </div>
                  </div>

                  {/* Score */}
                  <div>
                    <span className={`font-extrabold text-[16px] tabular-nums
                      ${ratio >= 0.9 ? "text-amber-500"
                        : ratio >= 0.75 ? "text-emerald-600"
                        : ratio >= 0.5 ? "text-blue-600"
                        : "text-gray-600"}`}>
                      {fmtScore(entry.best_score, examType)}
                    </span>
                    <span className="text-[11px] text-gray-400">{fmtMax(examType, config.v1ScoreMax)}</span>
                  </div>

                  {/* Percentile */}
                  <div className={`text-[13px] font-bold ${isMe ? "text-blue-600" : "text-emerald-600"}`}>
                    {pctLabel(idx, total)}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer note */}
        {!loading && (
          <div className="text-center text-[12px] text-gray-400 mt-6">
            Scores are from practice tests only. Rankings for &ldquo;This week&rdquo; reset every Monday 00:00 UTC.
            {" "}<Link href="/settings" className="text-blue-500 underline hover:text-blue-400">Privacy settings</Link> · Opt out of public rankings any time.
          </div>
        )}
      </div>
    </div>
  );
}
